/**
 * As camadas do CPTu: as sugestões de camadas, a estatística por camada, o perfil médio da campanha e a
 * estatística das unidades (o perfil típico) — spec 07 §5.6; onda 313.
 *
 * SUGESTÃO, FORA DO RN-252. As camadas que valem no relatório são as que o engenheiro define (topo,
 * base, unidade, descrição); as daqui são sugestões que ele pede por botão e confirma ou não — nada
 * vira camada sozinho. Por isso este arquivo não tem gabarito, não entra na conformidade
 * (`conformidade.ts`) e não muda número nenhum do motor: `MOTOR_CPTU_VERSAO` é o mesmo. As escolhas que
 * o motor faz sem fonte vão marcadas no código com o rótulo e o id entre colchetes, cada uma uma das
 * `REGRAS_A_ASSINAR_CAMADAS`, que vão ao RT à parte da conformidade (a proposta de homologação do motor
 * é cópia da conformidade). Nenhuma equivalência com outro programa é alegada.
 *
 * De onde vêm os métodos (a origem; a forma exata de cada conta é a do motor, abaixo):
 *  · T-ratio — a estatística T de Webster (1973, «Automatic soil-boundary location from transect
 *    data»), usada por Wickremesinghe & Campanella (1991, «Statistical methods for soil layer boundary
 *    location using the cone penetration test») para achar fronteiras de camada no CPTu;
 *  · agrupamento contíguo — o agrupamento hierárquico de Ward (1963, «Hierarchical grouping to optimize
 *    an objective function»), pelo menor aumento da soma dos quadrados dentro dos grupos, com a
 *    restrição de juntar só grupos vizinhos em profundidade (CONISS, Grimm 1987, «CONISS: a FORTRAN 77
 *    program for stratigraphically constrained cluster analysis by the method of incremental sum of
 *    squares»).
 *
 * O T-ratio (`tRatio`, `sugerirPorTRatio`), sobre o Ic do resultado ou, pedido, sobre log₁₀ qt (escolha 9):
 *
 *  1. A conta — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.tratio-forma]: em cada leitura, a janela se divide em duas metades iguais em volta dela — a
 *     de cima, [z − janela/2, z), e a de baixo, [z, z + janela/2) (com o passo de 1 cm e a janela de
 *     0,50 m, 25 leituras em cada) — e T = |x̄₁ − x̄₂| / √(s₁²/n₁ + s₂²/n₂), a estatística de duas
 *     amostras com os desvios amostrais, sobre os Ic com número. NaN onde a janela não cabe inteira na
 *     vertical, onde uma metade tem menos de 3 valores e onde o denominador é 0 (as duas metades
 *     constantes). As fronteiras são os picos locais de T com T ≥ limite, na profundidade da leitura
 *     do pico: é ali que começa a camada de baixo.
 *  2. A janela com que a tela começa, 0,50 m — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 —
 *     A ASSINAR PELO RT [cam.tratio-janela] (`TRATIO_INICIAL`); no motor a janela é parâmetro.
 *  3. O limite com que a tela começa, 10 — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A
 *     ASSINAR PELO RT [cam.tratio-limite] (`TRATIO_INICIAL`), escrito no campo da tela e na regra: não
 *     é «padrão» de fonte nenhuma. No motor o limite é obrigatório: sem ele, nenhuma fronteira e um
 *     aviso — não há valor escondido.
 *  4. O espaçamento — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.tratio-espacamento]: de dois picos mais próximos que o espaçamento mínimo fica o maior (no
 *     empate, o mais raso), escolhidos do maior para o menor; o espaçamento mínimo começa na metade da
 *     janela.
 *
 * O que a onda 347 acrescentou ao T-ratio (spec «o 3D com camadas» §5.1), todo pedido por parâmetro — sem
 * ele, o T-ratio é o de cima, igual, com os mesmos parâmetros:
 *
 *  9. A grandeza log₁₀ qt — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.tratio-qt]: com `grandeza: 'log_qt'`, a série do T é log₁₀ qt (NaN onde o qt não é positivo, que
 *     fica fora das metades), com a mesma conta da escolha 1; a tela começa com a janela de 2,00 m e o
 *     limite 6 (`TRATIO_INICIAL_QT`).
 * 10. A espessura mínima — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.espessura-minima]: depois dos picos, a camada mais fina que `espessuraMinima` (no empate, a mais
 *     rasa) junta-se à vizinha cuja média da grandeza usada (Ic ou log₁₀ qt) é a mais parecida com a dela
 *     (no empate, ou sem média que compare, à de cima; no topo, à de baixo), e repete até nenhuma ficar
 *     abaixo do mínimo; a vertical inteira mais rasa que o mínimo fica uma camada só. A tela começa com
 *     0,50 m (`ESPESSURA_MINIMA_INICIAL`).
 * 11. Juntar vizinhas da mesma zona — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR
 *     PELO RT [cam.juntar-zona]: depois da espessura mínima, com `juntarPorZona`, as camadas vizinhas com a
 *     mesma zona SBTn predominante (`zonaPredominante`) viram uma só; a camada sem zona não se junta. A tela
 *     começa com ele desligado.
 * 12. A unidade pela zona — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.unidade-pela-zona]: a unidade proposta de cada camada sugerida (`comUnidadePelaZona`) é a zona
 *     SBTn predominante nela — a mais frequente das leituras em [topo, base), no empate a que aparece mais
 *     rasa — escrita «Zona n · nome»; sem zona, «Sem zona». O engenheiro a troca antes de gravar.
 *
 * O agrupamento contíguo (`sugerirPorAgrupamento`), sobre log₁₀ qt e log₁₀ Fr do resultado:
 *
 *  5. Os blocos — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.agrupamento-blocos]: a vertical se divide em blocos de 5 cm de profundidade, pelos
 *     centímetros inteiros da leitura (o bloco da leitura é floor(round(100·z) / 5)); cada bloco vale a
 *     média de log₁₀ qt e a de log₁₀ Fr das leituras dele, e pesa 1 no agrupamento.
 *  6. A padronização — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.agrupamento-padronizacao]: log₁₀ qt e log₁₀ Fr pela média e pelo desvio amostral das
 *     leituras da vertical, para as duas pesarem igual; a que não varia na vertical vale 0 (fica fora).
 *  7. O bloco sem valor — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.agrupamento-bloco-sem-valor]: o bloco sem log₁₀ qt ou sem log₁₀ Fr (nenhum qt ou Fr com
 *     número positivo — o fs = 0 dos primeiros centímetros do CPTu01 do 2609350) junta-se ao vizinho de
 *     cima, e no topo da vertical ao de baixo: não abre camada nem buraco.
 *  8. O número de grupos — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT
 *     [cam.agrupamento-grupos]: de 2 a 12, pelo maior salto do custo. O custo W(k) é a soma dos
 *     quadrados dentro dos grupos com k grupos (Ward); juntar k grupos em k − 1 custa W(k − 1) − W(k),
 *     e o k escolhido é o em que esse custo mais salta em relação ao de juntar k + 1 em k — o maior
 *     [W(k − 1) − W(k)] − [W(k) − W(k + 1)], o cotovelo da curva custo × grupos (no empate, o menor
 *     k). O maior W(k − 1) − W(k) sozinho quase sempre daria k = 2: o custo de juntar só cresce. Quem
 *     pede um número de grupos o tem, até o número de blocos. Nos dois casos, a fronteira na última
 *     leitura sai (a camada não teria espessura, `fronteirasComEspessura`): pode sair um grupo a menos que
 *     o escolhido ou o pedido, com aviso.
 *
 * A estatística (`estatisticaPorCamada`, `estatisticaDasUnidades`): n, média, desvio amostral (n − 1),
 * mínimo, máximo e CV = desvio/|média| das leituras com número (NaN fica fora da conta; com n < 2 o
 * desvio e o CV são NaN; com média 0, o CV), de qt, fs, u₂, Ic, Su, φ', Dr, M e γ
 * (`GRANDEZAS_DA_ESTATISTICA`). A camada é [topo, base): a leitura da fronteira é da camada de baixo, e a
 * última leitura da vertical, quando nenhuma camada a contém em [topo, base), entra na camada que acaba
 * nela (a que começa nela a tem, e ela não conta duas vezes). A unidade se compara sem caixa e sem os
 * espaços das pontas.
 *
 * As sugestões só dão camada com espessura: a fronteira na última leitura sai, e ela fica com a camada de
 * cima (`fronteirasComEspessura`, com aviso).
 *
 * O perfil médio (`perfilMedio`): por profundidade ou por cota, em faixas de `passo` metros contadas
 * pelos centímetros inteiros — a faixa da leitura é floor(round(100·z) / round(100·passo)), nunca
 * floor(z/passo), que põe a leitura de 0,30 m com passo 0,10 na faixa 2 (0,3/0,1 = 2,9999…). Em cada
 * faixa, a média de cada vertical e, entre as verticais, a média e o desvio amostral dessas médias; n =
 * as verticais com valor na faixa.
 *
 * Como o resto do motor: sem dependência, sem relógio, sem sorteio; «não se aplica» é NaN; nada lança —
 * o parâmetro que não serve devolve a sugestão vazia com o aviso.
 */
import { ZONAS_SBTN } from './classificacao';
import type { ZonaSbtn } from './classificacao';
import type { Grandeza, RegraAAssinar, Resultado } from './tipos';

/** De onde veio a camada: digitada pelo engenheiro ou aceita de uma sugestão. */
export type OrigemDaCamada = 'manual' | 'sugestao_tratio' | 'sugestao_agrupamento';

/** Uma camada da vertical, em [topo, base) (m de profundidade). */
export interface Camada {
  topo: number;
  base: number;
  unidade: string;
  descricao?: string | null;
  origem: OrigemDaCamada;
}

/**
 * Uma sugestão de camadas: as fronteiras (m, crescentes, na profundidade de uma leitura) e as camadas
 * contíguas da primeira à última leitura (unidades C1 … Cn), os parâmetros com que saiu e os avisos.
 * Sem leituras ou com um parâmetro que não serve, nenhuma camada.
 */
export interface SugestaoDeCamadas {
  metodo: 'tratio' | 'agrupamento';
  fronteiras: number[];
  camadas: Camada[];
  parametros: Record<string, number>;
  avisos: string[];
}

/** A estatística de uma grandeza num conjunto de leituras. */
export interface EstatisticaDaGrandeza {
  n: number;
  media: number;
  desvio: number;
  minimo: number;
  maximo: number;
  cv: number;
}

/** Folga de ponto flutuante nas comparações de profundidade. */
const FOLGA = 1e-9;

/**
 * Os valores com que a tela começa o T-ratio — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 —
 * A ASSINAR PELO RT [cam.tratio-janela] e [cam.tratio-limite]: a janela de 0,50 m e o limite 10,
 * escritos nos campos da tela. `sugerirPorTRatio` não os usa: lá os dois são obrigatórios.
 */
export const TRATIO_INICIAL: Readonly<{ janela: number; limite: number }> = Object.freeze({ janela: 0.5, limite: 10 });

/**
 * Os valores com que a tela começa o T-ratio sobre log₁₀ qt — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA
 * DO RN-252 — A ASSINAR PELO RT [cam.tratio-qt]: a janela de 2,00 m e o limite 6 (os da prévia da onda 347).
 */
export const TRATIO_INICIAL_QT: Readonly<{ janela: number; limite: number }> = Object.freeze({ janela: 2, limite: 6 });

/**
 * A espessura mínima com que a tela começa, 0,50 m — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 —
 * A ASSINAR PELO RT [cam.espessura-minima]. No motor ela só vale quando pedida.
 */
export const ESPESSURA_MINIMA_INICIAL = 0.5;

/**
 * Os parâmetros do T-ratio: a janela (m), o limite de T e o espaçamento mínimo entre picos (m; metade da
 * janela, se não vier); a grandeza da série (`'ic'`, se não vier: o T-ratio da onda 313); a espessura mínima
 * (m) e o «juntar vizinhas da mesma zona», que só valem pedidos.
 */
export interface ParametrosDoTRatio {
  janela: number;
  limite: number;
  espacamentoMinimo?: number;
  grandeza?: 'ic' | 'log_qt';
  espessuraMinima?: number;
  juntarPorZona?: boolean;
}

/** Blocos de 5 cm — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT [cam.agrupamento-blocos]. */
const BLOCO_DO_AGRUPAMENTO = 0.05;

/** De 2 a 12 grupos — ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT [cam.agrupamento-grupos]. */
const MINIMO_DE_GRUPOS = 2;
const MAXIMO_DE_GRUPOS = 12;

/** Menos que isto de valores numa metade da janela não dá média nem desvio que sirvam. */
const MINIMO_NA_METADE = 3;

/** Mais faixas que isto não é perfil de CPTu (2 km com o passo de 1 cm): o perfil médio sai vazio. */
const FAIXAS_NO_MAXIMO = 200_000;

/** As grandezas da estatística por camada (spec §5.6): a lista é fixa. */
export const GRANDEZAS_DA_ESTATISTICA: readonly Grandeza[] = Object.freeze([
  'qt', 'fs', 'u2', 'ic', 'su', 'phi', 'dr', 'm', 'gama',
] as Grandeza[]);

/** «1.234,5» — o número escrito como a tela escreve: ponto no milhar, vírgula decimal (a notação científica fica). */
function brasileiro(texto: string): string {
  if (/e/i.test(texto)) return texto.replace('.', ',');
  const [inteira, decimais] = texto.split('.');
  const comMilhar = inteira.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decimais === undefined ? comMilhar : `${comMilhar},${decimais}`;
}

/** 1.211,62 — a profundidade com duas casas. */
const metros = (z: number): string => brasileiro(z.toFixed(2));

/** O número sem casas a mais (1.788; 7,5). */
const numero = (v: number): string => brasileiro(String(v));

/** «1 bloco» ou «1.200 blocos». */
const plural = (n: number, um: string, varios: string): string => `${numero(n)} ${n === 1 ? um : varios}`;

/** A estatística de uma grandeza nos valores dados; o que não é número fica fora. */
function estatisticaDosValores(valores: ArrayLike<number>): EstatisticaDaGrandeza {
  // A média desloca pelo primeiro valor: valores iguais dão a média igual a eles e o desvio 0, exatos.
  let n = 0;
  let deslocamento = 0;
  let soma = 0;
  let minimo = Infinity;
  let maximo = -Infinity;
  for (let i = 0; i < valores.length; i++) {
    const x = valores[i];
    if (!Number.isFinite(x)) continue;
    if (n === 0) deslocamento = x;
    n++;
    soma += x - deslocamento;
    if (x < minimo) minimo = x;
    if (x > maximo) maximo = x;
  }
  if (n === 0) return { n: 0, media: NaN, desvio: NaN, minimo: NaN, maximo: NaN, cv: NaN };
  const media = deslocamento + soma / n;
  let quadrados = 0;
  for (let i = 0; i < valores.length; i++) {
    const x = valores[i];
    if (Number.isFinite(x)) quadrados += (x - media) * (x - media);
  }
  const desvio = n < 2 ? NaN : Math.sqrt(quadrados / (n - 1));
  const cv = n < 2 || media === 0 ? NaN : desvio / Math.abs(media);
  return { n, media, desvio, minimo, maximo, cv };
}

/** A maior profundidade com número (a última leitura da vertical); NaN sem nenhuma. */
function maisFunda(z: ArrayLike<number>): number {
  let fundo = NaN;
  for (let i = 0; i < z.length; i++) if (Number.isFinite(z[i]) && !(z[i] <= fundo)) fundo = z[i];
  return fundo;
}

/** A camada válida contém z em [topo, base). */
const contem = (c: Pick<Camada, 'topo' | 'base'>, z: number): boolean =>
  Number.isFinite(c?.topo) && Number.isFinite(c?.base) && c.topo < c.base && z >= c.topo - FOLGA && z < c.base - FOLGA;

/**
 * A última leitura da vertical (`fundo`) entra na camada que acaba nela só quando nenhuma camada a contém em
 * [topo, base): a camada que começa nela a tem, e a de cima não a conta de novo.
 */
const fundoSemCamada = (camadas: readonly Pick<Camada, 'topo' | 'base'>[], fundo: number): boolean =>
  Number.isFinite(fundo) && !camadas.some((c) => contem(c, fundo));

/**
 * Os índices das leituras na camada [topo, base): a da fronteira é da camada de baixo; com `fundoSolto`
 * (nenhuma camada contém a última leitura), a última leitura da vertical (`fundo`) entra na camada que acaba
 * nela.
 */
function indicesDaCamada(z: ArrayLike<number>, topo: number, base: number, fundo: number, fundoSolto: boolean): number[] {
  const indices: number[] = [];
  if (!(Number.isFinite(topo) && Number.isFinite(base) && topo < base)) return indices;
  for (let i = 0; i < z.length; i++) {
    const zi = z[i];
    if (!(zi >= topo - FOLGA)) continue;
    if (zi < base - FOLGA || (fundoSolto && zi >= fundo - FOLGA && zi <= base + FOLGA)) indices.push(i);
  }
  return indices;
}

/** Os valores de uma série nos índices dados (a série que falta dá NaN). */
function nosIndices(s: ArrayLike<number> | undefined, indices: readonly number[]): Float64Array {
  const saida = new Float64Array(indices.length).fill(NaN);
  if (s) for (let k = 0; k < indices.length; k++) saida[k] = s[indices[k]];
  return saida;
}

/**
 * As fronteiras que fazem camada com espessura: crescentes, depois da primeira leitura e antes da última. A
 * fronteira na última leitura (ou além dela) daria a camada [z, z], sem espessura: ela sai, a última leitura
 * fica com o grupo de cima, e o aviso diz. O agrupamento chega a isso quando a última leitura está sozinha no
 * bloco dela e o Ward a isola; o T-ratio não (a metade de baixo da janela pede 3 leituras), e a guarda vale
 * para os dois.
 */
function fronteirasComEspessura(z: ArrayLike<number>, fronteiras: readonly number[], avisos: string[]): number[] {
  const primeira = z[0];
  const ultima = z[z.length - 1];
  const ficam: number[] = [];
  let naUltima = false;
  for (const f of [...fronteiras].sort((a, b) => a - b)) {
    if (!(f > primeira + FOLGA)) continue;
    if (!(f < ultima - FOLGA)) {
      naUltima = true;
      continue;
    }
    if (ficam.length === 0 || f > ficam[ficam.length - 1] + FOLGA) ficam.push(f);
  }
  if (naUltima) {
    avisos.push(`a última leitura (${metros(ultima)} m) ficaria sozinha numa camada sem espessura: juntou-se à camada de cima`);
  }
  return ficam;
}

/** As camadas contíguas da primeira à última leitura, cortadas nas fronteiras (crescentes): C1 … Cn. */
function camadasDasFronteiras(primeira: number, ultima: number, fronteiras: readonly number[], origem: OrigemDaCamada): Camada[] {
  const bordas = [primeira, ...fronteiras, ultima];
  const camadas: Camada[] = [];
  for (let k = 0; k + 1 < bordas.length; k++) {
    camadas.push({ topo: bordas[k], base: bordas[k + 1], unidade: `C${k + 1}`, descricao: null, origem });
  }
  return camadas;
}

/* ── O T-ratio ───────────────────────────────────────────────────────────────────────────── */

/** Média, variância amostral e quantos valores com número nas leituras de `de` a `ate` (inclusive). */
function momentos(v: ArrayLike<number>, de: number, ate: number): { n: number; media: number; variancia: number } {
  let n = 0;
  let deslocamento = 0;
  let soma = 0;
  for (let j = de; j <= ate; j++) {
    const x = v[j];
    if (!Number.isFinite(x)) continue;
    if (n === 0) deslocamento = x;
    n++;
    soma += x - deslocamento;
  }
  if (n === 0) return { n, media: NaN, variancia: NaN };
  const media = deslocamento + soma / n;
  let quadrados = 0;
  for (let j = de; j <= ate; j++) {
    const x = v[j];
    if (Number.isFinite(x)) quadrados += (x - media) * (x - media);
  }
  return { n, media, variancia: n < 2 ? NaN : quadrados / (n - 1) };
}

/**
 * O T de cada leitura (a escolha 1 do cabeçalho, [cam.tratio-forma]): z em m, crescente, como o motor
 * entrega; v, a série (o Ic ou log₁₀ qt); a janela em m. NaN onde a janela não cabe inteira na vertical, onde uma
 * metade tem menos de 3 valores com número e onde o denominador é 0. A entrada não muda.
 */
export function tRatio(z: ArrayLike<number>, v: ArrayLike<number>, janela: number): Float64Array {
  const n = z.length;
  const t = new Float64Array(n).fill(NaN);
  if (!(Number.isFinite(janela) && janela > 0)) return t;
  const meia = janela / 2;
  let topo = Infinity;
  let fundo = -Infinity;
  for (let i = 0; i < n; i++) {
    if (!Number.isFinite(z[i])) continue;
    if (z[i] < topo) topo = z[i];
    if (z[i] > fundo) fundo = z[i];
  }
  // A série mais curta que a profundidade: o que falta é NaN.
  const serie: ArrayLike<number> = v.length >= n ? v : Float64Array.from({ length: n }, (_, j) => (j < v.length ? v[j] : NaN));
  for (let i = 0; i < n; i++) {
    const zi = z[i];
    if (!Number.isFinite(zi)) continue;
    if (zi - meia < topo - FOLGA || zi + meia > fundo + FOLGA) continue; // a janela não cabe
    // A metade de cima, [zi − meia, zi), e a de baixo, [zi, zi + meia).
    let de = i;
    while (de - 1 >= 0 && z[de - 1] >= zi - meia - FOLGA) de--;
    let ate = i;
    while (ate + 1 < n && z[ate + 1] < zi + meia - FOLGA) ate++;
    if (de > i - 1) continue; // nenhuma leitura em cima
    const cima = momentos(serie, de, i - 1);
    const baixo = momentos(serie, i, ate);
    if (cima.n < MINIMO_NA_METADE || baixo.n < MINIMO_NA_METADE) continue;
    const denominador = Math.sqrt(cima.variancia / cima.n + baixo.variancia / baixo.n);
    if (!(Number.isFinite(denominador) && denominador > 0)) continue;
    const ti = Math.abs(cima.media - baixo.media) / denominador;
    if (Number.isFinite(ti)) t[i] = ti;
  }
  return t;
}

/**
 * As fronteiras pelo T-ratio sobre o Ic do resultado ou, com `grandeza: 'log_qt'`, sobre log₁₀ qt (escolhas
 * 1 a 4 e 9 do cabeçalho): os picos locais de T com T ≥ `limite` (obrigatório — não há limite padrão), e de
 * dois picos mais próximos que `espacamentoMinimo` (m; metade da janela, se não vier) fica o maior. Depois,
 * pedidos, a espessura mínima (escolha 10) e o «juntar vizinhas da mesma zona» (escolha 11). Devolve as
 * fronteiras, as camadas contíguas da primeira à última leitura e os avisos; nunca lança. Nos parâmetros, as
 * chaves da onda 347 só entram quando pedidas: `grandezaLogQt` (1 = log₁₀ qt, 0 = Ic), `espessuraMinima` e
 * `juntarPorZona` (1 ou 0).
 */
export function sugerirPorTRatio(r: Resultado, x: ParametrosDoTRatio): SugestaoDeCamadas {
  const janela = x?.janela;
  const limite = x?.limite;
  // O espaçamento mínimo começa na metade da janela —
  // ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT [cam.tratio-espacamento].
  const espacamento = x?.espacamentoMinimo ?? janela / 2;
  const grandeza = x?.grandeza ?? 'ic';
  const espessuraMinima = x?.espessuraMinima;
  const juntarPorZona = x?.juntarPorZona === true;
  const parametros: Record<string, number> = { janela, limite, espacamentoMinimo: espacamento };
  if (x?.grandeza !== undefined) parametros.grandezaLogQt = grandeza === 'log_qt' ? 1 : 0;
  if (espessuraMinima !== undefined) parametros.espessuraMinima = espessuraMinima;
  if (x?.juntarPorZona !== undefined) parametros.juntarPorZona = juntarPorZona ? 1 : 0;
  const sugestao: SugestaoDeCamadas = { metodo: 'tratio', fronteiras: [], camadas: [], parametros, avisos: [] };
  const z = r?.col?.z ?? new Float64Array(0);
  const n = z.length;
  if (n === 0) {
    sugestao.avisos.push('a vertical não tem leituras: sem sugestão');
    return sugestao;
  }
  if (!(Number.isFinite(janela) && janela > 0)) {
    sugestao.avisos.push('a janela do T-ratio tem de ser um número positivo, em metros: sem sugestão');
    return sugestao;
  }
  if (!Number.isFinite(limite)) {
    sugestao.avisos.push('o T-ratio pede o limite (não há limite padrão): sem ele, nenhuma fronteira');
    return sugestao;
  }
  if (!(Number.isFinite(espacamento) && espacamento >= 0)) {
    sugestao.avisos.push('o espaçamento mínimo do T-ratio tem de ser um número não negativo, em metros: sem sugestão');
    return sugestao;
  }
  if (grandeza !== 'ic' && grandeza !== 'log_qt') {
    sugestao.avisos.push(`a grandeza do T-ratio é o Ic («ic») ou log qt («log_qt»): «${String(grandeza)}» não serve — sem sugestão`);
    return sugestao;
  }
  if (espessuraMinima !== undefined && !(Number.isFinite(espessuraMinima) && espessuraMinima >= 0)) {
    sugestao.avisos.push('a espessura mínima do T-ratio tem de ser um número não negativo, em metros: sem sugestão');
    return sugestao;
  }

  // A série do T: o Ic, ou log₁₀ qt (NaN onde o qt não é positivo) —
  // ESCOLHA DO MOTOR, SEM FONTE — SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT [cam.tratio-qt].
  const qt = r.col.qt;
  const serie: ArrayLike<number> = grandeza === 'log_qt'
    ? Float64Array.from({ length: n }, (_, i) => log10(qt ? qt[i] : NaN))
    : r.col.ic ?? new Float64Array(0);
  const t = tRatio(z, serie, janela);
  const extensao = maisFunda(z) - z[0];
  if (!t.some((ti) => Number.isFinite(ti))) {
    sugestao.avisos.push(janela > extensao + FOLGA
      ? `a janela do T-ratio (${metros(janela)} m) não cabe na vertical (${metros(extensao)} m de leituras): nenhuma fronteira`
      : `nenhuma leitura com T: falta ${grandeza === 'log_qt' ? 'qt positivo' : 'Ic com número'} em pelo menos 3 leituras `
        + 'de cada metade da janela');
  }

  // Os picos locais (no patamar, o primeiro) com T ≥ limite.
  const anterior = (i: number): number => (i > 0 && Number.isFinite(t[i - 1]) ? t[i - 1] : -Infinity);
  const seguinte = (i: number): number => (i + 1 < n && Number.isFinite(t[i + 1]) ? t[i + 1] : -Infinity);
  const picos: number[] = [];
  for (let i = 0; i < n; i++) {
    if (Number.isFinite(t[i]) && t[i] >= limite && t[i] > anterior(i) && t[i] >= seguinte(i)) picos.push(i);
  }
  // Do maior para o menor (no empate, o mais raso): fica o pico sem outro já escolhido a menos do espaçamento.
  picos.sort((a, b) => t[b] - t[a] || a - b);
  const escolhidos: number[] = [];
  for (const i of picos) {
    if (escolhidos.every((j) => Math.abs(z[i] - z[j]) >= espacamento - FOLGA)) escolhidos.push(i);
  }
  let fronteiras = fronteirasComEspessura(z, escolhidos.map((i) => z[i]), sugestao.avisos);
  if (fronteiras.length === 0 && t.some((ti) => Number.isFinite(ti))) {
    sugestao.avisos.push(`nenhum pico de T chega ao limite ${numero(limite)}: uma camada só`);
  }
  if (espessuraMinima !== undefined) {
    fronteiras = comEspessuraMinima(z, serie, fronteiras, espessuraMinima, sugestao.avisos);
  }
  if (juntarPorZona) fronteiras = juntandoPorZona(r, fronteiras, sugestao.avisos);
  sugestao.fronteiras = fronteiras;
  sugestao.camadas = camadasDasFronteiras(z[0], z[n - 1], fronteiras, 'sugestao_tratio');
  return sugestao;
}

/** A média da série nas leituras de [topo, base) (a última leitura da vertical, `fundo`, na camada que acaba nela). */
function mediaNaCamada(z: ArrayLike<number>, serie: ArrayLike<number>, topo: number, base: number, fundo: number): number {
  return estatisticaDosValores(nosIndices(serie, indicesDaCamada(z, topo, base, fundo, true))).media;
}

/**
 * A espessura mínima (escolha 10, [cam.espessura-minima]): a camada mais fina que `minima` (no empate, a mais
 * rasa) junta-se à vizinha de média da série mais parecida — no empate, ou sem média que compare, à de cima;
 * no topo, à de baixo —, e repete até nenhuma ficar abaixo do mínimo ou sobrar uma camada só. Devolve as
 * fronteiras que ficam; o aviso diz quantas camadas se juntaram.
 */
function comEspessuraMinima(
  z: ArrayLike<number>, serie: ArrayLike<number>, fronteiras: readonly number[], minima: number, avisos: string[],
): number[] {
  const fundo = maisFunda(z);
  const bordas = [z[0], ...fronteiras, z[z.length - 1]];
  let juntadas = 0;
  while (bordas.length > 2) {
    let fina = -1;
    for (let k = 0; k + 1 < bordas.length; k++) {
      const e = bordas[k + 1] - bordas[k];
      if (e < minima - FOLGA && (fina < 0 || e < bordas[fina + 1] - bordas[fina] - FOLGA)) fina = k;
    }
    if (fina < 0) break;
    const media = (k: number): number => mediaNaCamada(z, serie, bordas[k], bordas[k + 1], fundo);
    const distancia = (k: number): number => {
      const d = Math.abs(media(k) - media(fina));
      return Number.isFinite(d) ? d : Infinity;
    };
    const ultima = bordas.length - 2;
    const comACima = fina > 0 && (fina === ultima || distancia(fina - 1) <= distancia(fina + 1));
    // Juntar à de cima tira a borda do topo da fina; à de baixo, a da base.
    bordas.splice(comACima ? fina : fina + 1, 1);
    juntadas++;
  }
  if (juntadas > 0) {
    avisos.push(`${plural(juntadas, 'camada', 'camadas')} mais ${juntadas === 1 ? 'fina' : 'finas'} que a espessura mínima `
      + `(${metros(minima)} m) ${juntadas === 1 ? 'juntou-se' : 'juntaram-se'} à vizinha de média mais parecida`);
  }
  if (bordas.length === 2 && bordas[1] - bordas[0] < minima - FOLGA) {
    avisos.push(`a vertical inteira (${metros(bordas[1] - bordas[0])} m de leituras) é mais rasa que a espessura mínima `
      + `(${metros(minima)} m): uma camada só`);
  }
  return bordas.slice(1, -1);
}

/**
 * «Juntar vizinhas da mesma zona» (escolha 11, [cam.juntar-zona]): sai a fronteira entre duas camadas com a
 * mesma zona SBTn predominante (a de cada camada antes de juntar); a camada sem zona não se junta.
 */
function juntandoPorZona(r: Resultado, fronteiras: readonly number[], avisos: string[]): number[] {
  const z = r.col.z;
  const bordas = [z[0], ...fronteiras, z[z.length - 1]];
  const zonas = bordas.slice(0, -1).map((topo, k) => zonaPredominante(r, topo, bordas[k + 1]));
  const ficam = fronteiras.filter((_, k) => zonas[k] === null || zonas[k] !== zonas[k + 1]);
  const sairam = fronteiras.length - ficam.length;
  if (sairam > 0) {
    avisos.push(`${plural(sairam, 'fronteira', 'fronteiras')} entre vizinhas da mesma zona SBTn `
      + `${sairam === 1 ? 'saiu' : 'saíram'}: as camadas se juntaram`);
  }
  return ficam;
}

/**
 * A zona SBTn (`r.sbtn`, 1 a 9) predominante nas leituras de [topo, base) — escolha 12,
 * [cam.unidade-pela-zona]: a mais frequente; no empate, a que aparece mais rasa. A zona 0 (sem zona) não
 * conta; `null` quando nenhuma leitura do trecho tem zona.
 */
export function zonaPredominante(r: Resultado, topo: number, base: number): number | null {
  const z = r?.col?.z;
  const sbtn = r?.sbtn;
  if (!z || !sbtn || !(Number.isFinite(topo) && Number.isFinite(base) && topo < base)) return null;
  const vezes = new Map<number, { n: number; primeira: number }>();
  for (let i = 0; i < z.length && i < sbtn.length; i++) {
    if (!(z[i] >= topo - FOLGA && z[i] < base - FOLGA)) continue;
    const zona = sbtn[i];
    if (!(zona >= 1 && zona <= 9)) continue;
    const v = vezes.get(zona);
    if (v) v.n++;
    else vezes.set(zona, { n: 1, primeira: z[i] });
  }
  let melhor: number | null = null;
  let maior = { n: 0, primeira: Infinity };
  for (const [zona, v] of vezes) {
    if (v.n > maior.n || (v.n === maior.n && v.primeira < maior.primeira)) {
      melhor = zona;
      maior = v;
    }
  }
  return melhor;
}

/**
 * As camadas com a unidade pela zona SBTn predominante (escolha 12, [cam.unidade-pela-zona]): «Zona n · nome»,
 * e «Sem zona» quando o trecho não tem zona. Topo, base, descrição e origem ficam; a entrada não muda.
 */
export function comUnidadePelaZona(camadas: readonly Camada[], r: Resultado): Camada[] {
  return camadas.map((c) => {
    const zona = zonaPredominante(r, c.topo, c.base);
    const unidade = zona === null ? 'Sem zona' : `Zona ${zona} · ${ZONAS_SBTN[zona as ZonaSbtn].nome}`;
    return { ...c, unidade };
  });
}

/* ── O agrupamento contíguo ──────────────────────────────────────────────────────────────── */

/** Um bloco de leituras (índices de `de` a `ate`) com as somas de log₁₀ qt e log₁₀ Fr que têm número. */
interface Bloco {
  de: number;
  ate: number;
  somaQt: number;
  nQt: number;
  somaFr: number;
  nFr: number;
}

/** log₁₀ de um número positivo; NaN no resto (o log de 0 ou de negativo não existe). */
const log10 = (x: number): number => (Number.isFinite(x) && x > 0 ? Math.log10(x) : NaN);

/** A média e o desvio amostral de log₁₀ da série, nas leituras com número positivo. */
function mediaEDesvio(s: ArrayLike<number> | undefined, n: number): { media: number; desvio: number } {
  const logs = new Float64Array(n).fill(NaN);
  if (s) for (let i = 0; i < n; i++) logs[i] = log10(s[i]);
  const e = estatisticaDosValores(logs);
  return { media: e.media, desvio: e.desvio };
}

/**
 * As fronteiras pelo agrupamento contíguo de Ward sobre log₁₀ qt e log₁₀ Fr (escolhas 5 a 8 do
 * cabeçalho): blocos de `bloco` m (5 cm), padronizados, juntados de dois em dois vizinhos pelo menor
 * aumento da soma dos quadrados dentro dos grupos até sobrar um grupo; o número de grupos é o pedido
 * (`grupos`) ou o do maior salto do custo, de 2 a `maximo` (12). `custos` traz W(k) de 1 grupo até
 * `maximo` + 1 (o que o salto do último usa). Nunca lança: o parâmetro que não serve dá a sugestão vazia.
 */
export function sugerirPorAgrupamento(
  r: Resultado,
  x?: { grupos?: number; maximo?: number; bloco?: number },
): SugestaoDeCamadas & { custos: { grupos: number; custo: number }[] } {
  const bloco = x?.bloco ?? BLOCO_DO_AGRUPAMENTO;
  const maximo = x?.maximo ?? MAXIMO_DE_GRUPOS;
  const pedidos = x?.grupos;
  const sugestao: SugestaoDeCamadas & { custos: { grupos: number; custo: number }[] } = {
    metodo: 'agrupamento', fronteiras: [], camadas: [], parametros: { bloco, maximo }, avisos: [], custos: [],
  };
  const z = r?.col?.z ?? new Float64Array(0);
  const n = z.length;
  const centimetros = Math.round(bloco * 100);
  if (n === 0) {
    sugestao.avisos.push('a vertical não tem leituras: sem sugestão');
    return sugestao;
  }
  // O bloco é de centímetros inteiros (as faixas contam os centímetros da leitura): o que não é recusa, e o
  // que é vai aos parâmetros como foi usado.
  if (!(Number.isFinite(bloco) && centimetros >= 1 && Math.abs(bloco * 100 - centimetros) < 1e-6)) {
    sugestao.avisos.push(`o bloco do agrupamento é de centímetros inteiros, de 0,01 m em diante: ${
      Number.isFinite(bloco) ? `${numero(bloco)} m` : 'sem número'} não serve — sem sugestão`);
    return sugestao;
  }
  sugestao.parametros.bloco = centimetros / 100;
  if (!(Number.isInteger(maximo) && maximo >= MINIMO_DE_GRUPOS)) {
    sugestao.avisos.push(`o máximo de grupos tem de ser um inteiro de ${MINIMO_DE_GRUPOS} em diante: sem sugestão`);
    return sugestao;
  }
  if (pedidos !== undefined && !(Number.isInteger(pedidos) && pedidos >= 1)) {
    sugestao.avisos.push('o número de grupos pedido tem de ser um inteiro de 1 em diante: sem sugestão');
    return sugestao;
  }

  // Os blocos, na ordem das leituras (escolha 5, [cam.agrupamento-blocos]).
  const qt = r.col.qt;
  const fr = r.col.fr;
  const crus: Bloco[] = [];
  let chave = NaN;
  for (let i = 0; i < n; i++) {
    if (!Number.isFinite(z[i])) continue;
    const k = Math.floor(Math.round(z[i] * 100) / centimetros);
    if (k !== chave) {
      crus.push({ de: i, ate: i, somaQt: 0, nQt: 0, somaFr: 0, nFr: 0 });
      chave = k;
    }
    const b = crus[crus.length - 1];
    b.ate = i;
    const lq = log10(qt ? qt[i] : NaN);
    const lf = log10(fr ? fr[i] : NaN);
    if (Number.isFinite(lq)) { b.somaQt += lq; b.nQt++; }
    if (Number.isFinite(lf)) { b.somaFr += lf; b.nFr++; }
  }

  // O bloco sem valor junta-se ao de cima; no topo, ao de baixo — ESCOLHA DO MOTOR, SEM FONTE —
  // SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT [cam.agrupamento-bloco-sem-valor].
  const temValor = (b: Bloco): boolean => b.nQt > 0 && b.nFr > 0;
  const juntar = (a: Bloco, b: Bloco): void => {
    a.de = Math.min(a.de, b.de);
    a.ate = Math.max(a.ate, b.ate);
    a.somaQt += b.somaQt; a.nQt += b.nQt;
    a.somaFr += b.somaFr; a.nFr += b.nFr;
  };
  const blocos: Bloco[] = [];
  const doTopo: Bloco[] = [];
  let semValor = 0;
  for (const b of crus) {
    if (temValor(b)) {
      blocos.push({ ...b });
      for (const t of doTopo) juntar(blocos[0], t);
      doTopo.length = 0;
    } else {
      semValor++;
      if (blocos.length > 0) juntar(blocos[blocos.length - 1], b);
      else doTopo.push(b);
    }
  }
  if (blocos.length === 0) {
    sugestao.avisos.push('nenhum bloco com log qt e log Fr: sem sugestão');
    return sugestao;
  }
  if (semValor > 0) {
    sugestao.avisos.push(`${plural(semValor, 'bloco', 'blocos')} sem log qt ou log Fr `
      + `${semValor === 1 ? 'juntou-se' : 'juntaram-se'} ao vizinho de cima (no topo da vertical, ao de baixo)`);
  }

  // A padronização pela média e o desvio das leituras da vertical — ESCOLHA DO MOTOR, SEM FONTE —
  // SUGESTÃO, FORA DO RN-252 — A ASSINAR PELO RT [cam.agrupamento-padronizacao].
  const pq = mediaEDesvio(qt, n);
  const pf = mediaEDesvio(fr, n);
  const padronizar = (m: number, p: { media: number; desvio: number }): number =>
    p.desvio > 0 ? (m - p.media) / p.desvio : 0;

  // Ward contíguo: cada grupo com os blocos (peso 1 cada) e o centro; junta os vizinhos de menor custo.
  type Grupo = { inicio: number; pesos: number; cx: number; cy: number };
  const grupos: Grupo[] = blocos.map((b, k) => ({
    inicio: k, pesos: 1, cx: padronizar(b.somaQt / b.nQt, pq), cy: padronizar(b.somaFr / b.nFr, pf),
  }));
  const total = grupos.length;
  const quantosGuardar = Math.max(maximo, pedidos ?? 0) + 1;
  const custo = new Float64Array(total + 1); // custo[k] = W(k); W(total) = 0
  const particoes = new Map<number, number[]>([[total, grupos.map((g) => g.inicio)]]);
  let w = 0;
  while (grupos.length > 1) {
    let melhor = 0;
    let menor = Infinity;
    for (let j = 0; j + 1 < grupos.length; j++) {
      const a = grupos[j];
      const b = grupos[j + 1];
      const d = (a.pesos * b.pesos) / (a.pesos + b.pesos) * ((a.cx - b.cx) ** 2 + (a.cy - b.cy) ** 2);
      if (d < menor) { menor = d; melhor = j; }
    }
    const a = grupos[melhor];
    const b = grupos[melhor + 1];
    const pesos = a.pesos + b.pesos;
    grupos.splice(melhor, 2, {
      inicio: a.inicio, pesos, cx: (a.pesos * a.cx + b.pesos * b.cx) / pesos, cy: (a.pesos * a.cy + b.pesos * b.cy) / pesos,
    });
    w += menor;
    custo[grupos.length] = w;
    if (grupos.length <= quantosGuardar) particoes.set(grupos.length, grupos.map((g) => g.inicio));
  }

  // Quantos grupos: o pedido, ou o do maior salto do custo, de 2 a 12 (escolha 8, [cam.agrupamento-grupos]).
  let k: number;
  if (pedidos !== undefined) {
    k = Math.min(pedidos, total);
  } else if (total < MINIMO_DE_GRUPOS) {
    k = total;
    sugestao.avisos.push('a vertical tem um bloco só: uma camada só');
  } else {
    const W = (g: number): number => custo[Math.min(g, total)];
    k = MINIMO_DE_GRUPOS;
    let maiorSalto = -Infinity;
    for (let g = MINIMO_DE_GRUPOS; g <= Math.min(maximo, total); g++) {
      const salto = (W(g - 1) - W(g)) - (W(g) - W(g + 1));
      if (salto > maiorSalto) { maiorSalto = salto; k = g; }
    }
  }

  for (let g = 1; g <= Math.min(total, maximo + 1); g++) sugestao.custos.push({ grupos: g, custo: custo[g] });

  const inicios = particoes.get(k) ?? [];
  const daGuarda: string[] = [];
  sugestao.fronteiras = fronteirasComEspessura(z, inicios.slice(1).map((b) => z[blocos[b].de]), daGuarda);
  sugestao.camadas = camadasDasFronteiras(z[0], z[n - 1], sugestao.fronteiras, 'sugestao_agrupamento');
  // Os grupos que saíram (a guarda pode ter juntado a última leitura ao grupo de cima).
  sugestao.parametros.grupos = sugestao.camadas.length;
  if (pedidos !== undefined && pedidos > total) {
    sugestao.avisos.push(`pediram ${numero(pedidos)} grupos e a vertical tem ${plural(total, 'bloco', 'blocos')}: `
      + `${plural(sugestao.camadas.length, 'grupo', 'grupos')}`);
  }
  sugestao.avisos.push(...daGuarda);
  return sugestao;
}

/* ── A estatística por camada, o perfil médio e as unidades ──────────────────────────────── */

/**
 * A estatística de cada camada [topo, base) da vertical (a última leitura, se nenhuma camada a contém,
 * entra na que acaba nela), nas grandezas dadas (as nove de `GRANDEZAS_DA_ESTATISTICA`, se não vierem), na
 * ordem delas.
 */
export function estatisticaPorCamada(
  r: Resultado,
  camadas: readonly Pick<Camada, 'topo' | 'base'>[],
  grandezas: readonly Grandeza[] = GRANDEZAS_DA_ESTATISTICA,
): { topo: number; base: number; leituras: number; estatistica: Record<string, EstatisticaDaGrandeza> }[] {
  const z = r?.col?.z ?? new Float64Array(0);
  const fundo = maisFunda(z);
  const fundoSolto = fundoSemCamada(camadas, fundo);
  return camadas.map((c) => {
    const indices = indicesDaCamada(z, c.topo, c.base, fundo, fundoSolto);
    const estatistica: Record<string, EstatisticaDaGrandeza> = {};
    for (const g of grandezas) estatistica[g] = estatisticaDosValores(nosIndices(r?.col?.[g], indices));
    return { topo: c.topo, base: c.base, leituras: indices.length, estatistica };
  });
}

/**
 * O perfil médio de uma grandeza nas verticais, por profundidade ou por cota (cota da boca − z), em
 * faixas de `passo` m pelos centímetros inteiros: em cada faixa, a média de cada vertical e, entre elas,
 * a média e o desvio amostral (NaN com uma vertical só); n = as verticais com valor na faixa. As faixas
 * vão de cima para baixo (a profundidade crescendo, a cota caindo), sem pular faixa; `centro` é o meio
 * de cada uma. Por cota, a vertical sem cota fica de fora, e o código dela vai em `semCota`.
 */
export function perfilMedio(
  verticais: readonly { codigo: string; r: Resultado; cota: number | null }[],
  x: { grandeza: Grandeza; eixo: 'profundidade' | 'cota'; passo: number },
): { centro: Float64Array; media: Float64Array; desvio: Float64Array; n: Int32Array; semCota: string[] } {
  const porCota = x?.eixo === 'cota';
  const semCota = porCota ? verticais.filter((v) => !Number.isFinite(v.cota)).map((v) => v.codigo) : [];
  const vazio = {
    centro: new Float64Array(0), media: new Float64Array(0), desvio: new Float64Array(0), n: new Int32Array(0), semCota,
  };
  const passo = Math.round((x?.passo ?? NaN) * 100);
  if (!(Number.isFinite(passo) && passo >= 1)) return vazio;

  // A média de cada vertical em cada faixa.
  const medias: Map<number, number>[] = [];
  let kMinimo = Infinity;
  let kMaximo = -Infinity;
  for (const v of verticais) {
    if (porCota && !Number.isFinite(v.cota)) continue;
    const z = v.r?.col?.z;
    const s = v.r?.col?.[x.grandeza];
    const faixas = new Map<number, number[]>();
    if (z && s) {
      for (let i = 0; i < z.length; i++) {
        const coordenada = porCota ? (v.cota as number) - z[i] : z[i];
        if (!Number.isFinite(coordenada) || !Number.isFinite(s[i])) continue;
        const k = Math.floor(Math.round(coordenada * 100) / passo);
        const lista = faixas.get(k);
        if (lista) lista.push(s[i]);
        else faixas.set(k, [s[i]]);
      }
    }
    const daVertical = new Map<number, number>();
    for (const [k, valores] of faixas) {
      daVertical.set(k, estatisticaDosValores(valores).media);
      if (k < kMinimo) kMinimo = k;
      if (k > kMaximo) kMaximo = k;
    }
    medias.push(daVertical);
  }
  if (!(kMaximo >= kMinimo) || kMaximo - kMinimo + 1 > FAIXAS_NO_MAXIMO) return vazio;

  const quantas = kMaximo - kMinimo + 1;
  const saida = {
    centro: new Float64Array(quantas), media: new Float64Array(quantas), desvio: new Float64Array(quantas),
    n: new Int32Array(quantas), semCota,
  };
  for (let p = 0; p < quantas; p++) {
    const k = porCota ? kMaximo - p : kMinimo + p; // de cima para baixo
    const valores = medias.filter((m) => m.has(k)).map((m) => m.get(k) as number);
    const e = estatisticaDosValores(valores);
    saida.centro[p] = ((2 * k + 1) * passo) / 200;
    saida.media[p] = e.media;
    saida.desvio[p] = e.desvio;
    saida.n[p] = e.n;
  }
  return saida;
}

/**
 * A estatística das unidades em todas as verticais (o perfil típico): as camadas com a mesma unidade —
 * comparada sem caixa e sem os espaços das pontas — juntam as leituras delas, de todas as verticais.
 * `unidade` é o rótulo da primeira camada em que a unidade aparece (sem os espaços das pontas);
 * `verticais`, os códigos em que ela aparece, na ordem; camada sem unidade fica de fora.
 */
export function estatisticaDasUnidades(
  verticais: readonly { codigo: string; r: Resultado; camadas: readonly Camada[] }[],
  grandezas: readonly Grandeza[] = GRANDEZAS_DA_ESTATISTICA,
): { unidade: string; verticais: string[]; leituras: number; estatistica: Record<string, EstatisticaDaGrandeza> }[] {
  type Unidade = { unidade: string; verticais: string[]; indices: Map<number, Set<number>> };
  const unidades = new Map<string, Unidade>();
  verticais.forEach((v, posicao) => {
    const z = v.r?.col?.z ?? new Float64Array(0);
    const fundo = maisFunda(z);
    const fundoSolto = fundoSemCamada(v.camadas ?? [], fundo);
    for (const c of v.camadas ?? []) {
      const rotulo = String(c?.unidade ?? '').trim();
      if (!rotulo) continue;
      const chave = rotulo.toLowerCase();
      let u = unidades.get(chave);
      if (!u) {
        u = { unidade: rotulo, verticais: [], indices: new Map() };
        unidades.set(chave, u);
      }
      if (!u.verticais.includes(v.codigo)) u.verticais.push(v.codigo);
      let daVertical = u.indices.get(posicao);
      if (!daVertical) {
        daVertical = new Set();
        u.indices.set(posicao, daVertical);
      }
      for (const i of indicesDaCamada(z, c.topo, c.base, fundo, fundoSolto)) daVertical.add(i);
    }
  });
  return [...unidades.values()].map((u) => {
    let leituras = 0;
    for (const s of u.indices.values()) leituras += s.size;
    const estatistica: Record<string, EstatisticaDaGrandeza> = {};
    for (const g of grandezas) {
      const valores: number[] = [];
      for (const [posicao, indices] of u.indices) {
        const s = verticais[posicao].r?.col?.[g];
        for (const i of [...indices].sort((a, b) => a - b)) valores.push(s ? s[i] : NaN);
      }
      estatistica[g] = estatisticaDosValores(valores);
    }
    return { unidade: u.unidade, verticais: u.verticais, leituras, estatistica };
  });
}

/* ── As regras que o RT assina ───────────────────────────────────────────────────────────── */

/** A evidência de toda escolha das camadas: não há gabarito nem fonte para ela — é sugestão. */
const SEM_FONTE = 'escolha do motor, sem fonte — sugestão, fora do RN-252';

/**
 * As escolhas das sugestões de camadas (1 a 12 do cabeçalho), uma por rótulo do comentário, com o mesmo
 * id entre colchetes. Vão ao RT pelo doc da onda 313 e, na 315, no anexo do relatório — NÃO pela
 * conformidade (`conformidade.ts`): as camadas são sugestão, fora do RN-252. O `298.64` confere que a
 * lista e os rótulos são os mesmos, e o `300.25`, o resto.
 */
export const REGRAS_A_ASSINAR_CAMADAS: readonly RegraAAssinar[] = Object.freeze(
  [
    {
      id: 'cam.tratio-forma',
      arquivo: 'camadas.ts',
      descricao: 'O T-ratio da sugestão é a estatística de duas amostras T = |x̄₁ − x̄₂| / √(s₁²/n₁ + s₂²/n₂) sobre o '
        + 'Ic, com desvios amostrais, e a janela dividida em duas metades iguais em volta da leitura: a de cima, '
        + '[z − janela/2, z), e a de baixo, [z, z + janela/2). T só onde a janela cabe inteira na vertical e cada '
        + 'metade tem pelo menos 3 valores; as fronteiras são os picos locais de T com T ≥ limite, na profundidade '
        + 'da leitura do pico. A estatística T é de Webster (1973), usada por Wickremesinghe & Campanella (1991) '
        + 'para as fronteiras de camada no CPTu; esta forma é a do motor.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.tratio-janela',
      arquivo: 'camadas.ts',
      descricao: 'A tela começa o T-ratio com a janela de 0,50 m (25 leituras em cada metade com o passo de 1 cm); o '
        + 'engenheiro a muda no campo. No motor a janela é parâmetro.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.tratio-limite',
      arquivo: 'camadas.ts',
      descricao: 'A tela começa o T-ratio com o limite 10 (o pico de T ≥ 10 é fronteira), escrito no campo — não é '
        + 'padrão de fonte nenhuma. No motor o limite é obrigatório: sem ele, nenhuma fronteira, com aviso.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.tratio-espacamento',
      arquivo: 'camadas.ts',
      descricao: 'De dois picos de T mais próximos que o espaçamento mínimo fica o maior (no empate, o mais raso), '
        + 'escolhidos do maior para o menor; o espaçamento mínimo começa na metade da janela.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.agrupamento-blocos',
      arquivo: 'camadas.ts',
      descricao: 'O agrupamento contíguo trabalha em blocos de 5 cm de profundidade, pelos centímetros inteiros da '
        + 'leitura, cada bloco com a média de log₁₀ qt e a de log₁₀ Fr das leituras dele e peso 1; junta dois '
        + 'blocos ou grupos vizinhos de cada vez, pelo menor aumento da soma dos quadrados dentro dos grupos (Ward '
        + 'com restrição de contiguidade; CONISS, Grimm 1987).',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.agrupamento-grupos',
      arquivo: 'camadas.ts',
      descricao: 'O número de grupos do agrupamento vai de 2 a 12 e sai do maior salto do custo: o k em que o custo '
        + 'de juntar k grupos em k − 1 mais passa o de juntar k + 1 em k, [W(k − 1) − W(k)] − [W(k) − W(k + 1)], '
        + 'com W a soma dos quadrados dentro dos grupos (o cotovelo da curva custo × grupos; no empate, o menor k). '
        + 'O engenheiro pode pedir outro número (até o número de blocos). Nos dois casos, a fronteira na última '
        + 'leitura sai (a camada não teria espessura): pode sair um grupo a menos que o escolhido ou o pedido, com '
        + 'aviso.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.agrupamento-padronizacao',
      arquivo: 'camadas.ts',
      descricao: 'Antes de agrupar, log₁₀ qt e log₁₀ Fr são padronizados pela média e pelo desvio amostral das '
        + 'leituras da vertical, para as duas pesarem igual; a variável que não varia na vertical vale 0.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.agrupamento-bloco-sem-valor',
      arquivo: 'camadas.ts',
      descricao: 'O bloco sem log₁₀ qt ou sem log₁₀ Fr (nenhum qt ou Fr com número positivo, como o fs = 0 dos '
        + 'primeiros centímetros) junta-se ao vizinho de cima; no topo da vertical, ao de baixo — não abre camada '
        + 'nem buraco.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.tratio-qt',
      arquivo: 'camadas.ts',
      descricao: 'O T-ratio pode correr sobre log₁₀ qt em vez do Ic (o qt que não é positivo fica fora das metades da '
        + 'janela), com a mesma conta; a tela começa esse T-ratio com a janela de 2,00 m e o limite 6. Sem a grandeza '
        + 'pedida, o T-ratio é sobre o Ic.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.espessura-minima',
      arquivo: 'camadas.ts',
      descricao: 'Depois dos picos, a camada sugerida mais fina que a espessura mínima (no empate, a mais rasa) '
        + 'junta-se à vizinha cuja média da grandeza do T-ratio (Ic ou log₁₀ qt) é a mais parecida com a dela — no '
        + 'empate, ou sem média que compare, à de cima; no topo, à de baixo —, e repete até nenhuma ficar abaixo do '
        + 'mínimo; a vertical inteira mais rasa que o mínimo fica uma camada só. A tela começa com 0,50 m.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.juntar-zona',
      arquivo: 'camadas.ts',
      descricao: 'Pedido («juntar vizinhas da mesma zona»), depois da espessura mínima: as camadas sugeridas vizinhas '
        + 'com a mesma zona SBTn predominante viram uma só; a camada sem zona não se junta. A tela começa com ele '
        + 'desligado.',
      evidencia: SEM_FONTE,
    },
    {
      id: 'cam.unidade-pela-zona',
      arquivo: 'camadas.ts',
      descricao: 'A unidade proposta de cada camada sugerida é a zona SBTn predominante nela — a mais frequente das '
        + 'leituras em [topo, base), no empate a que aparece mais rasa —, escrita «Zona n · nome»; sem zona, «Sem '
        + 'zona». O engenheiro a troca antes de gravar.',
      evidencia: SEM_FONTE,
    },
  ].map((r) => Object.freeze(r)),
);
